import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box } from '@wix/design-system';
import { dataHooks } from '../../../dataHooks';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText, ThemedTextButton } from '../../ThemedComponents';
import { DealerOfferCard, NotNowLink } from './DealerOfferCard';

/**
 * redesigned "confirm your account email" Dealer offer (wix-2025): shows the account
 * email, Confirm sends a code, and "Change email" opens the change-email step.
 */
export const ConfirmAccountEmailRedesign: React.FC = observer(() => {
  const {
    rootStore: {
      confirmEmailStore,
      postLoginStore,
      displayStore: { preset },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();

  return (
    <DealerOfferCard
      title={t('post.login.confirm.account.email.title')}
      subtitle={t('post.login.confirm.account.email.subtitle')}
      primaryButton={
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          onClick={() =>
            !confirmEmailStore.isSubmitted && confirmEmailStore.confirmEmail()
          }
          showLoader={confirmEmailStore.isSubmitted}
        >
          {t('post.login.confirm')}
        </ButtonWithLoader>
      }
      skip={<NotNowLink onClick={confirmEmailStore.showLater} />}
    >
      <Box direction="vertical" width="100%" gap={2}>
        <ThemedText>{t('post.login.your.account.email')}</ThemedText>
        <ThemedText
          weight="bold"
          dir="ltr"
          dataHook={dataHooks.postLogin.confirmEmail.email}
        >
          {postLoginStore.userDetails?.email}
        </ThemedText>
        <Box gap="4px" verticalAlign="middle">
          <Trans i18nKey="post.login.change.email.link">
            <ThemedText size="small" secondary>
              Not your current email address?
            </ThemedText>
            <ThemedTextButton
              dataHook={dataHooks.postLogin.confirmEmail.changeEmailLink}
              as="a"
              size="small"
              underline="always"
              onClick={confirmEmailStore.changeEmail}
              skin="standard"
            >
              Change Email
            </ThemedTextButton>
          </Trans>
        </Box>
      </Box>
    </DealerOfferCard>
  );
});
